import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import InputField from "common/customInputField";
import Button from "react-bootstrap/Button";
import SelectInput from "common/selectInput";
import DataTable from "common/desktopDataTable";
import CustomTooltip from "common/tooltip";
import alertIcon from "svg/red-alert-icon.svg";
import { displayDesktopRowItem } from "../../common/desktopDataTable/utils";
import { searchProductsService, getProductCategoryService } from "services/common.service";
import { showSpinner, hideSpinner } from "store/slices/spinner";
import warning from "svg/warning.svg";
import heartIcon from "svg/heart.svg";
import heartFilledIcon from "svg/heart_filled.svg";
import { api } from "store/slices/api";
import sortBy from "lodash/sortBy";
import "./style.scss";

const searchProductsHeaders = [
  {
    id: 1,
    name: "Fav",
    keyName: "Favorite",
    valueName: "Favorite",
  },
  {
    id: 2,
    name: "Product Name",
    keyName: "Product Name",
    valueName: "productName",
    dataKey: "productName",
    sortable: true,
  },
  {
    id: 3,
    name: "Sales #",
    keyName: "Sales#",
    valueName: "salesNumber",
    dataKey: "salesNumber",
    sortable: true,
  },
  {
    id: 4,
    name: "Manufacturer",
    keyName: "Manufacturer",
    valueName: "manufacturer",
    dataKey: "manufacturer",
    sortable: true,
  },
  {
    id: 5,
    name: "Manufacturer Part #",
    keyName: "ManufacturerPart#",
    valueName: "vendorPartNumber",
    dataKey: "vendorPartNumber",
    sortable: true,
  },
  {
    id: 6,
    name: "Category",
    keyName: "Category",
    valueName: "category",
    dataKey: "category",
    sortable: true,
  },
];

const searchProductsExpandedItems = [
  { keyName: "Department", valueName: "department", classNameOne: "m-b-16" },
  { keyName: "Sales #", valueName: "salesNumber", classNameOne: "m-b-16" },
  { keyName: "Product/Rex", valueName: "rex", classNameOne: "m-b-16" },
  { keyName: "Manufacturer", valueName: "manufacturer", classNameOne: "m-b-16" },
  { keyName: "Manufacturer part #", valueName: "manufacturerPartNumber", classNameOne: "m-b-16" },
  { keyName: "UPC", valueName: "upc", classNameOne: "m-b-16" },
  { keyName: "Category", valueName: "category", classNameOne: "m-b-16" },
];

const ProductLookup = ({ currentProduct, onProductSelectionCallback, isPriceQuote, isHideSearchAfterSelection }) => {
  const dispatch = useDispatch();
  const prevCountRef = useRef();
  const initialState = {
    department: { description: "All Departments", id: "" },
    category: { description: "", id: "" },
    productName: "",
    salesNumber: "",
    productRex: "",
    manufacturer: "",
    vendorPartNumber: "",
    upc: "",
  };

  const [searchFields, setSearchFields] = useState({ ...initialState });
  const [searchResults, setSearchResults] = useState([]);
  const [searchFilterValue, setSearchFilterValue] = useState("");
  const [productCategoryList, setProductCategoryList] = useState([]);
  const [showLookupTable, setShowLookupTable] = useState(false);

  const { data: departmentsList } = api.useGetDepartmentsQuery();
  const [updateFavorites] = api.useDeprecatedUpdateFavoriteProductsMutation();
  const { sub } = useSelector((state) => state.loggedInUser);

  const departmentNodeRef = useRef();
  const categoryNodeRef = useRef();

  const tempFilterList = searchResults.filter((row) => {
    const columnsTemp = ["department", "productName", "salesNumber", "rex", "manufacturer", "category", "upc"];
    return columnsTemp.some((column) => {
      const variable = row[column] == null ? "" : row[column].toString();
      return (
        variable
          .replace(/[^a-zA-Z0-9]/g, "")
          .toLowerCase()
          .indexOf(searchFilterValue.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()) > -1
      );
    });
  });
  const filteredTableList = tempFilterList;

  const handleSearchFilter = (e) => {
    const { value } = e.target;
    setSearchFilterValue(value);
  };

  useEffect(() => {
    departmentsList?.some((item) => {
      if (item.id == "") {
        setSearchFields({ ...searchFields, department: { ...item } });
      }
      return item.id === "";
    });
  }, [departmentsList]);

  useEffect(() => {
    if (searchFields?.department?.id && searchFields?.department?.description) {
      getProductCategoryService(searchFields?.department?.id).then((res) => {
        const catagories = res.map((item, index) => {
          return {
            id: index,
            description: item,
          };
        });
        setProductCategoryList([...catagories]);
        setTimeout(() => {
          setSearchFields({ ...searchFields, category: { description: "All Categories", id: "" } });
        }, 0);
      });
    } else if (!searchFields?.department.id && searchFields?.department.description === "All Departments") {
      setProductCategoryList([]);
      setTimeout(() => {
        setSearchFields({ ...searchFields, category: { description: "All Categories", id: "" } });
      }, 0);
    }
  }, [searchFields.department]);

  const handleSearchFieldsChange = (e, a) => {
    const { id, value } = e.target;
    if (id === "department" || id === "category") {
      setSearchFields({ ...searchFields, [id]: { description: a.description, id: a.id } });
    } else {
      setSearchFields({ ...searchFields, [id]: value });
    }
  };

  const handleSearch = (isPersistData, isFavoritesOnly) => {
    dispatch(showSpinner());
    if (!isPersistData) setSearchResults([]);
    const searchRequestObj = {
      department: !searchFields.department.id ? "" : searchFields.department.id,
      upc: searchFields.upc,
      salesNumber: searchFields.salesNumber.replace(/^0+/, "").replace(/[^a-zA-Z0-9]/g, ""),
      manufacturer: searchFields.manufacturer,
      vendorPartNumber: searchFields.vendorPartNumber,
      rex: searchFields.productRex,
      productName: searchFields.productName,
      createdBy: sub,
      onlyFavourite: isFavoritesOnly,
      category: searchFields.category.description === "All Categories" ? "" : searchFields.category.description,
    };

    searchProductsService(searchRequestObj)
      .then((resp) => {
        setShowLookupTable(true);
        setSearchResults(isFavoritesOnly ? sortBy(resp, "category") : resp);
        dispatch(hideSpinner());
      })
      .catch((err) => {
        console.error(err);
        dispatch(hideSpinner());
      });
  };

  const onProductSelection = (productData, isSelected) => {
    onProductSelectionCallback(productData, isSelected);
    if (isHideSearchAfterSelection) {
      setSearchFields({ ...initialState });
      setSearchResults([]);
    }
  };

  const onFavoriteClick = (productData) => {
    dispatch(showSpinner());
    const favoriteRequestObj = {
      salesNumber: productData.salesNumber,
      createdBy: sub,
      favourite: productData.favourite === "TRUE" ? "FALSE" : "TRUE",
    };
    updateFavorites(favoriteRequestObj)
      .then(() => {
        handleSearch(true);
      })
      .catch((err) => {
        console.error(err);
        dispatch(hideSpinner());
      });
  };
  const handleProductSelect = (e, row, isMobileSelection) => {
    if (isMobileSelection) {
      const { id, checked } = e.target;
      if (id !== prevCountRef.current && document.getElementById(prevCountRef.current)) {
        document.getElementById(prevCountRef.current).checked = false;
      }
      prevCountRef.current = id;
      onProductSelection(checked ? { ...row } : {}, checked ? true : false);
    } else {
      const { id, dataset } = e.currentTarget;
      let checked;
      if (document.getElementById(id).dataset.ischecked == "true") {
        document.getElementById(id).dataset.ischecked = "false";
        document.getElementById(id).classList.remove("selected");
      } else {
        document.getElementById(id).dataset.ischecked = "true";
        document.getElementById(id).classList.add("selected");
      }
      checked = document.getElementById(id).dataset.ischecked;
      if (id !== prevCountRef.current && document.getElementById(prevCountRef.current)) {
        document.getElementById(prevCountRef.current).dataset.ischecked = false;
        document.getElementById(prevCountRef.current).classList.remove("selected");
      }
      prevCountRef.current = id;
      onProductSelection(checked == "true" ? { ...row } : {}, checked == "true" ? true : false);
    }
  };

  const renderDesktopProductLookupRows = (currentData) => {
    return currentData.map((row) => (
      <tr key={row.rex + row.upc} className="table-root__body__tr">
        {searchProductsHeaders.map((col, index) => {
          if (col.name === "Fav") {
            return (
              <td
                className="table-root__body__tr__td table-root__body__tr__td__fixed-row"
                key={`${row.rex}-${row.upc}--${index}`}
              >
                <Button variant="link" className="m-0 p-0 d-inline-flex align-items-center">
                  <img
                    src={row.favourite === "TRUE" ? heartFilledIcon : heartIcon}
                    onClick={() => onFavoriteClick(row)}
                  />
                </Button>
              </td>
            );
          } else if (col.name === "Product Name") {
            return (
              <td
                className="table-root__body__tr__td table-root__body__tr__td__text"
                key={`${row.rex}-${row.upc}--${index}`}
              >
                <span
                  className="product-name-link"
                  id={row.salesNumber}
                  data-ischecked={false}
                  onClick={(e) => handleProductSelect(e, row)}
                >
                  <CustomTooltip placement="top" trigger="hover" text={`${row.productName}`}>
                    <span>{displayDesktopRowItem(row, col)}</span>
                  </CustomTooltip>
                </span>
              </td>
            );
          }

          return (
            <td
              className={"table-root__body__tr__td table-root__body__tr__td__text"}
              key={`${row.rex}-${row.upc}--${index}`}
            >
              <span>{displayDesktopRowItem(row, col)}</span>
            </td>
          );
        })}
      </tr>
    ));
  };

  return (
    <div className="container-fluid p-2 border-0 m-0 search-products">
      <div className="search-form-container__fields">
        <div className="d-flex search-form-container__fields__msg">
          <div className="search-form-container__fields__msg--icon">
            <img src={warning} alt={"warning-icon"} />
          </div>
          <div className="search-form-container__fields__msg--text">At least one input is required</div>
        </div>
        {currentProduct && (
          <div>
            <span className="search-products-title my-3">Current Product</span>
            <div className="d-flex flex-column flex-md-row gap-5 product-view">
              <div className="d-flex flex-row flex-md-column justify-content-between justify-content-md-start product-attribute">
                <span className="label">SW Sales Number</span>
                <span
                  className={`info-item ${
                    !currentProduct.isManualProduct && !currentProduct.salesNumber ? "highLightSales" : ""
                  }`}
                >
                  {currentProduct.isManualProduct || !currentProduct.salesNumber ? "NA" : currentProduct.salesNumber}
                  {!currentProduct.isManualProduct && !currentProduct.salesNumber && (
                    <CustomTooltip placement="top" trigger="hover" text={"Sales Number is empty"}>
                      <span>
                        <img className="alert_style" src={alertIcon} />
                      </span>
                    </CustomTooltip>
                  )}
                </span>
              </div>
              <div className="divider d-flex d-none d-md-block"></div>
              <div className="d-flex flex-row flex-md-column justify-content-between justify-content-md-start product-attribute">
                <span className="label">Product Name</span>
                <span className="info-item">
                  {currentProduct.isManualProduct || !currentProduct.id ? "NA" : currentProduct.id}
                </span>
              </div>
              <div className="divider d-flex d-none d-md-block"></div>
              <div className="d-flex flex-row flex-md-column justify-content-between justify-content-md-start product-attribute">
                <span className="label">SW Product Category</span>
                <span className="info-item">
                  {currentProduct.isManualProduct || !currentProduct.type ? "NA" : currentProduct.type}
                </span>
              </div>
            </div>
          </div>
        )}
        <form className="search-form-container__fields__form">
          <div className="search-products-container">
            {!isPriceQuote && (
              <>
                <span className="search-products-title">Search Products </span>
                <hr className="mb-5" />
              </>
            )}
            <div className="row align-items-center">
              <div className="col-12 col-sm-12 col-md-6 col-lg-5 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  id="productName"
                  label="Product Name"
                  placeholder={"Enter Product Name"}
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={searchFields.productName}
                  onChange={(e) => handleSearchFieldsChange(e)}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-6 col-lg-5 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  id="salesNumber"
                  label="Sales#"
                  placeholder={"Enter Sales Number"}
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={searchFields.salesNumber}
                  onChange={(e) => handleSearchFieldsChange(e)}
                />
              </div>
            </div>
            <div className="row align-items-center">
              <div className="col-12 col-sm-12 col-md-6 col-lg-5 search-form-container__fields__form-group">
                <SelectInput
                  inputRef={departmentNodeRef}
                  options={departmentsList ?? []}
                  label="Department"
                  id="department"
                  value={searchFields.department.description}
                  handleSelectInput={(e, value) => handleSearchFieldsChange(e, value)}
                  showEmptyValue={false}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-6 col-lg-5 search-form-container__fields__form-group">
                <SelectInput
                  inputRef={categoryNodeRef}
                  options={productCategoryList ?? []}
                  label="Category"
                  id="category"
                  value={searchFields.category.description}
                  handleSelectInput={(e, value) => handleSearchFieldsChange(e, value)}
                  showEmptyValue={false}
                />
              </div>
            </div>
            <div className="row align-items-center">
              <div className="col-12 col-sm-12 col-md-6 col-lg-5 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  id="manufacturer"
                  label="Manufacturer"
                  placeholder={"Enter Manufacturer"}
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={searchFields.manufacturer}
                  onChange={(e) => handleSearchFieldsChange(e)}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-6 col-lg-5 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  id="vendorPartNumber"
                  label="Mfg Part Number"
                  placeholder={"Enter Mfg Part Number"}
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={searchFields.vendorPartNumber}
                  onChange={(e) => handleSearchFieldsChange(e)}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-sm-12 col-md-6 col-lg-5 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  id="upc"
                  label="UPC"
                  placeholder={"Enter UPC"}
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={searchFields.upc}
                  onChange={(e) => handleSearchFieldsChange(e)}
                />
              </div>
              <div className="col-12 col-sm-12 col-md-6 col-lg-5 search-form-container__fields__form-group">
                <InputField
                  type="text"
                  id="productRex"
                  label="Product/Rex"
                  placeholder={"Enter Product Rex"}
                  fieldClasses="form-control search-form-container__fields__form-group__item"
                  value={searchFields.productRex}
                  onChange={(e) => handleSearchFieldsChange(e)}
                />
              </div>
            </div>
            <div className="row align-items-center">
              <div className="col-12 col-sm-12 col-md-2 col-lg-2 search-form-container__fields__form-group me-3">
                <Button
                  onClick={handleSearch}
                  disabled={
                    !searchFields.productRex &&
                    !searchFields.upc &&
                    !searchFields.manufacturer &&
                    !searchFields.category.description &&
                    !searchFields.department.description &&
                    !searchFields.salesNumber &&
                    !searchFields.productName
                  }
                >
                  Search
                </Button>
              </div>
              <div className="col-12 col-sm-12 col-md-3 col-lg-3 search-form-container__fields__form-group">
                <Button
                  variant="outline-primary"
                  className="text-nowrap"
                  onClick={() => handleSearch(false, true)}
                  disabled={false}
                >
                  Show Favorites
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
      {showLookupTable && (
        <DataTable
          key={searchResults}
          tableHeader={searchProductsHeaders}
          tableBody={searchResults}
          parentComponent="productLookup"
          countTitle="Search Results"
          renderDesktopRows={renderDesktopProductLookupRows}
          displaySearchFilter={true}
          displaySectionTwo={false}
          isSelectAllChecked={false}
          filteredTableList={filteredTableList}
          searchFilterValue={searchFilterValue}
          handleSearchFilter={handleSearchFilter}
          expandedItems={searchProductsExpandedItems}
        />
      )}
    </div>
  );
};

export default ProductLookup;
